import cx from 'classnames';

import { TypographyVariant } from './typography';

type ScreenVariants = 'md' | 'lg' | 'xl' | 'xl2';

const baseVariant = {
  variantFontSizesAndLeading: {
    title1: 'text-h1', // 72px/88px
    title2: 'text-h2', // 64px/80px
    title3: 'text-h3', // 48px/60px
    title4: 'text-h4', // 32px/44px
    title5: 'text-h5', // 24px/32px
    title6: 'text-base leading-normal',
    subtitle1: 'text-base leading-normal',
    subtitle2: 'text-sm leading-10/7',
    body1: 'text-base leading-normal',
    body2: 'text-sm leading-10/7',
    small: 'text-xs leading-5/3',
  },
  variantLetterSpacing: {
    title1: 'tracking-normal',
    title2: 'tracking-md',
    title3: 'tracking-lg',
    title4: 'tracking-lg',
    title5: 'tracking-wider',
    title6: 'tracking-wider',
    subtitle1: 'tracking-xl2',
    subtitle2: 'tracking-xl2',
    body1: 'tracking-wider',
    body2: 'tracking-wider',
    small: 'tracking-normal',
  },
  variantThickness: {
    bold: 'font-bold',
    title1: 'font-medium',
    title2: 'font-medium',
    title3: 'font-medium',
    title4: 'font-medium',
    title5: 'font-medium',
    title6: 'font-medium',
    subtitle1: 'font-medium',
    subtitle2: 'font-medium',
    body1: 'font-normal',
    body2: 'font-normal',
    small: 'font-normal',
  },
};

const mdVariants = {
  variantFontSizesAndLeading: {
    title1: 'md:text-h1', // 72px/88px
    title2: 'md:text-h2', // 64px/80px
    title3: 'md:text-h3', // 48px/60px
    title4: 'md:text-h4', // 32px/44px
    title5: 'md:text-h5', // 24px/32px
    title6: 'md:text-base md:leading-normal',
    subtitle1: 'md:text-base md:leading-normal',
    subtitle2: 'md:text-sm md:leading-10/7',
    body1: 'md:text-base md:leading-normal',
    body2: 'md:text-sm md:leading-10/7',
    small: 'md:text-xs md:leading-5/3',
  },
  variantLetterSpacing: {
    title1: 'md:tracking-normal',
    title2: 'md:tracking-md',
    title3: 'md:tracking-lg',
    title4: 'md:tracking-lg',
    title5: 'md:tracking-wider',
    title6: 'md:tracking-wider',
    subtitle1: 'md:tracking-xl2',
    subtitle2: 'md:tracking-xl2',
    body1: 'md:tracking-wider',
    body2: 'md:tracking-wider',
    small: 'md:tracking-normal',
  },
  variantThickness: {
    bold: 'md:font-bold',
    title1: 'md:font-medium',
    title2: 'md:font-medium',
    title3: 'md:font-medium',
    title4: 'md:font-medium',
    title5: 'md:font-medium',
    title6: 'md:font-medium',
    subtitle1: 'md:font-medium',
    subtitle2: 'md:font-semibold',
    body1: 'md:font-normal',
    body2: 'md:font-normal',
    small: 'md:font-normal',
  },
};

const lgVariants = {
  variantFontSizesAndLeading: {
    title1: 'lg:text-h1', // 72px/88px
    title2: 'lg:text-h2', // 64px/80px
    title3: 'lg:text-h3', // 48px/60px
    title4: 'lg:text-h4', // 32px/44px
    title5: 'lg:text-h5', // 24px/32px
    title6: 'lg:text-base lg:leading-normal',
    subtitle1: 'lg:text-base lg:leading-normal',
    subtitle2: 'lg:text-sm lg:leading-10/7',
    body1: 'lg:text-base lg:leading-normal',
    body2: 'lg:text-sm lg:leading-10/7',
    small: 'lg:text-xs lg:leading-5/3',
  },
  variantLetterSpacing: {
    title1: 'lg:tracking-normal',
    title2: 'lg:tracking-md',
    title3: 'lg:tracking-lg',
    title4: 'lg:tracking-lg',
    title5: 'lg:tracking-wider',
    title6: 'lg:tracking-wider',
    subtitle1: 'lg:tracking-xl2',
    subtitle2: 'lg:tracking-xl2',
    body1: 'lg:tracking-wider',
    body2: 'lg:tracking-wider',
    small: 'lg:tracking-normal',
  },
  variantThickness: {
    bold: 'lg:font-medium',
    title1: 'lg:font-medium',
    title2: 'lg:font-medium',
    title3: 'lg:font-medium',
    title4: 'lg:font-medium',
    title5: 'lg:font-medium',
    title6: 'lg:font-medium',
    subtitle1: 'lg:font-medium',
    subtitle2: 'lg:font-medium',
    body1: 'lg:font-normal',
    body2: 'lg:font-normal',
    small: 'lg:font-normal',
  },
};

const xlVariants = {
  variantFontSizesAndLeading: {
    title1: 'xl:text-h1', // 72px/88px
    title2: 'xl:text-h2', // 64px/80px
    title3: 'xl:text-h3', // 48px/60px
    title4: 'xl:text-h4', // 32px/44px
    title5: 'xl:text-h5', // 24px/32px
    title6: 'xl:text-base xl:leading-normal',
    subtitle1: 'xl:text-base xl:leading-normal',
    subtitle2: 'xl:text-sm xl:leading-10/7',
    body1: 'xl:text-base xl:leading-normal',
    body2: 'xl:text-sm xl:leading-10/7',
    small: 'xl:text-xs xl:leading-5/3',
  },
  variantLetterSpacing: {
    title1: 'xl:tracking-normal',
    title2: 'xl:tracking-md',
    title3: 'xl:tracking-lg',
    title4: 'xl:tracking-lg',
    title5: 'xl:tracking-wider',
    title6: 'xl:tracking-wider',
    subtitle1: 'xl:tracking-xl2',
    subtitle2: 'xl:tracking-xl2',
    body1: 'xl:tracking-wider',
    body2: 'xl:tracking-wider',
    small: 'xl:tracking-normal',
  },
  variantThickness: {
    bold: 'xl:font-bold',
    title1: 'xl:font-medium',
    title2: 'xl:font-medium',
    title3: 'xl:font-medium',
    title4: 'xl:font-medium',
    title5: 'xl:font-medium',
    title6: 'xl:font-medium',
    subtitle1: 'xl:font-medium',
    subtitle2: 'xl:font-medium',
    body1: 'xl:font-normal',
    body2: 'xl:font-normal',
    small: 'xl:font-normal',
  },
};
const xl2Variants = {
  variantFontSizesAndLeading: {
    title1: 'xl2:text-h1', // 72px/88px
    title2: 'xl2:text-h2', // 64px/80px
    title3: 'xl2:text-h3', // 48px/60px
    title4: 'xl2:text-h4', // 32px/44px
    title5: 'xl2:text-h5', // 24px/32px
    title6: 'xl2:text-base xl2:leading-normal',
    subtitle1: 'xl2:text-base xl2:leading-normal',
    subtitle2: 'xl2:text-sm xl2:leading-10/7',
    body1: 'xl2:text-base xl2:leading-normal',
    body2: 'xl2:text-sm xl2:leading-10/7',
    small: 'xl2:text-xs xl2:leading-5/3',
  },
  variantLetterSpacing: {
    title1: 'xl2:tracking-normal',
    title2: 'xl2:tracking-md',
    title3: 'xl2:tracking-lg',
    title4: 'xl2:tracking-lg',
    title5: 'xl2:tracking-wider',
    title6: 'xl2:tracking-wider',
    subtitle1: 'xl2:tracking-xl2',
    subtitle2: 'xl2:tracking-xl2',
    body1: 'xl2:tracking-wider',
    body2: 'xl2:tracking-wider',
    small: 'xl2:tracking-normal',
  },
  variantThickness: {
    bold: 'xl2:font-bold',
    title1: 'xl2:font-medium',
    title2: 'xl2:font-medium',
    title3: 'xl2:font-medium',
    title4: 'xl2:font-medium',
    title5: 'xl2:font-medium',
    title6: 'xl2:font-medium',
    subtitle1: 'xl2:font-medium',
    subtitle2: 'xl2:font-medium',
    body1: 'xl2:font-normal',
    body2: 'xl2:font-normal',
    small: 'xl2:font-normal',
  },
};

const getTypographyStylesMQ = (
  variant: TypographyVariant,
  screen: ScreenVariants,
  bold?: boolean
): string => {
  let screenVariant = baseVariant;
  switch (screen) {
    case 'md':
      screenVariant = mdVariants;
      break;
    case 'lg':
      screenVariant = lgVariants;
      break;
    case 'xl':
      screenVariant = xlVariants;
      break;
    case 'xl2':
      screenVariant = xl2Variants;
      break;
    default:
      screenVariant = baseVariant;
      break;
  }

  const fontSizeLineHeight = variant
    ? screenVariant.variantFontSizesAndLeading[variant]
    : '';

  const letterSpacing = variant
    ? screenVariant.variantLetterSpacing[variant]
    : '';

  const thickness = bold
    ? screenVariant.variantThickness.bold
    : variant
      ? screenVariant.variantThickness[variant]
      : '';

  return cx(fontSizeLineHeight, letterSpacing, thickness);
};

export const getTypographyStylesAsClassName = (
  variant?: TypographyVariant,
  variantM?: TypographyVariant,
  variantL?: TypographyVariant,
  variantXL?: TypographyVariant,
  variantXXL?: TypographyVariant,
  bold?: boolean
): string => {
  const fontSizeLineHeight = variant
    ? baseVariant.variantFontSizesAndLeading[variant]
    : '';
  const letterSpacing = variant
    ? baseVariant.variantLetterSpacing[variant]
    : '';
  const thickness = bold
    ? baseVariant.variantThickness.bold
    : variant
      ? baseVariant.variantThickness[variant]
      : '';
  const mediaQueryM = variantM
    ? getTypographyStylesMQ(variantM, 'md', bold)
    : '';
  const mediaQueryL = variantL
    ? getTypographyStylesMQ(variantL, 'lg', bold)
    : '';
  const mediaQueryXL = variantXL
    ? getTypographyStylesMQ(variantXL, 'xl', bold)
    : '';
  const mediaQueryXXL = variantXXL
    ? getTypographyStylesMQ(variantXXL, 'xl2', bold)
    : '';

  return cx(
    fontSizeLineHeight,
    letterSpacing,
    thickness,
    mediaQueryM,
    mediaQueryL,
    mediaQueryXL,
    mediaQueryXXL
  );
};
